import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';

/** Duración de la transición entre preguntas: la misma que la de evaluación (`speed: 400`). */
const SLIDE_SPEED_MS = 400;

/**
 * Swiper espera a que Angular pinte el `<swiper-container>` antes de inicializarse; 200 ms es el
 * mismo margen que usa `evaluacion.page.ts` para lo mismo.
 */
const INIT_DELAY_MS = 200;

/** Slides vivas a cada lado de la activa: con la activa, como mucho 5 en el DOM. */
const ADD_SLIDES_BEFORE = 2;
const ADD_SLIDES_AFTER = 2;

/**
 * Encuesta una pregunta a la vez, con el motor **virtual** de Swiper.
 *
 * Replica el patrón probado en `pages/evaluacion/evaluacion.page.ts`: el módulo `virtual` solo
 * mantiene vivas unas pocas slides alrededor de la activa, así que una encuesta de 60 preguntas no
 * pone 60 componentes en el DOM (el problema que reventó iOS en evaluaciones). Por eso el cuerpo de
 * cada pregunta llega por `questionTemplate` y no por contenido proyectado: cada slide se crea y se
 * destruye según la ventana.
 *
 * Genérico sobre el objeto de pregunta de quien lo use: no conoce `PollQuestionView` ni la ruta.
 *
 * `index` es la fuente de verdad. Un cambio que viene de fuera (toolbar, panel) mueve el deslizador;
 * un cambio que viene del propio deslizador (gesto táctil, si se activa) se notifica por `indexChange`.
 */
@Component({
  selector: 'app-survey-slides',
  templateUrl: './survey-slides.component.html',
  styleUrls: ['./survey-slides.component.scss'],
})
export class SurveySlidesComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() questions: any[] = [];
  @Input() index = 0;
  @Input() labelOf: (question: any) => string = (question) => String(question?.label ?? '');
  @Input() answeredOf: (question: any) => boolean = () => false;
  /** Cuerpo de cada pregunta. Contexto: `{ $implicit: question, index }` (índice absoluto). */
  @Input() questionTemplate!: TemplateRef<any>;
  @Input() panelTitle = 'Preguntas';
  @Input() panelSectionLabel = 'Contenido';
  /** Ancho máximo de la tarjeta de encuesta. Por defecto el mismo que `max-w-[960px]`. */
  @Input() customMaxWidth = '960px';
  /**
   * Sin marco ni tarjeta: el componente no pinta fondo, trama, sombra ni esquinas, y la tarjeta ocupa
   * todo el alto (conserva `customMaxWidth` centrado). La superficie la pone quien lo aloja.
   */
  @Input() bleed = false;
  /** Encuesta de una ruta con el panel de la ruta a la derecha: el deck se angosta entre 1024px y 1380px. */
  @Input() roadRightMode = false;
  /** Gesto táctil. Desktop navega por flechas y toolbar, así que va apagado por defecto. */
  @Input() allowTouchMove = false;

  @Output() indexChange = new EventEmitter<number>();

  @ViewChild('deck') deckRef?: ElementRef<any>;

  /** Lo que Swiper entrega a `renderExternal`: qué slides están montadas y dónde. */
  virtualData: { slides: any[]; offset: number; from: number; to: number } = {
    slides: [],
    offset: 0,
    from: 0,
    to: 0,
  };

  panelOpen = false;

  private swiper: any = null;
  private initTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private ngZone: NgZone, private cdr: ChangeDetectorRef) {}

  get canGoPrev(): boolean {
    return this.index > 0;
  }

  get canGoNext(): boolean {
    return this.index < this.questions.length - 1;
  }

  ngAfterViewInit(): void {
    // Como en evaluación: init diferido, una vez que Angular ya pintó el elemento.
    this.initTimer = setTimeout(() => this.initSwiper(), INIT_DELAY_MS);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.swiper) return;

    if (changes['questions'] && !changes['questions'].firstChange) {
      this.replaceSlides();
    }
    if (changes['index'] && !changes['index'].firstChange && this.swiper.activeIndex !== this.index) {
      this.swiper.slideTo(this.index, SLIDE_SPEED_MS);
    }
  }

  ngOnDestroy(): void {
    if (this.initTimer) clearTimeout(this.initTimer);
    this.swiper?.destroy?.(true, true);
    this.swiper = null;
  }

  go(index: number): void {
    if (index < 0 || index >= this.questions.length || index === this.index) return;
    this.indexChange.emit(index);
  }

  onPanelSelect(index: number): void {
    this.panelOpen = false;
    this.go(index);
  }

  /**
   * Current and answered both fill with primary; pending stays grey. Not `bg-primary/40` for answered:
   * Tailwind cannot apply an opacity modifier to a `var(--primary)` colour, so that class generated
   * nothing and the segment rendered transparent.
   */
  segmentClass(index: number): string {
    if (index === this.index || this.answeredOf(this.questions[index])) return 'bg-primary';
    return 'bg-gray-200';
  }

  trackBySlide(index: number, question: any): number | string {
    return question?.id ?? index;
  }

  private initSwiper(): void {
    const el = this.deckRef?.nativeElement;
    if (!el || this.swiper) return;

    // Parámetros imperativos, no bindings: el módulo virtual se configura antes de `initialize()`.
    Object.assign(el, {
      slidesPerView: 1,
      speed: SLIDE_SPEED_MS,
      allowTouchMove: this.allowTouchMove,
      initialSlide: this.index,
      observer: true,
      observeParents: true,
      virtual: {
        slides: this.questions,
        renderExternal: (data: typeof this.virtualData) => {
          // Swiper llama fuera de la zona de Angular. `detectChanges()` y no `markForCheck()`: el
          // ancestro puede ser OnPush y no estar marcado sucio.
          this.ngZone.run(() => {
            this.virtualData = data;
            this.cdr.detectChanges();
          });
        },
        addSlidesBefore: ADD_SLIDES_BEFORE,
        addSlidesAfter: ADD_SLIDES_AFTER,
      },
    });

    el.initialize();
    this.swiper = el.swiper;

    el.addEventListener('swiperslidechange', () => {
      this.ngZone.run(() => this.onSwiperSlideChange());
    });
  }

  private onSwiperSlideChange(): void {
    const active: number = this.swiper.activeIndex;
    if (active !== this.index) {
      this.index = active;
      this.indexChange.emit(active);
    }
    this.cdr.markForCheck();
  }

  /** Otra encuesta llegó con preguntas nuevas: Swiper vuelve a leer la lista y repinta la ventana. */
  private replaceSlides(): void {
    this.swiper.params.virtual.slides = this.questions;
    this.swiper.virtual.slides = this.questions;
    this.swiper.virtual.update(true);
  }
}
